import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'react-apollo';
import { noop } from 'lodash';

import { ModalTypeIds, ExclusionModalTypeIds } from './constants';
import { SpecialtyIds, procedureShape } from '../../../constants';
import checkConstraints from './checkConstraints';
import ConstraintModalContainer from './ConstraintModalContainer';
import { withSpecialtyProcedures } from '../../../gql';
import getDisplayName from '../../../utils/displayName';

export default function addConstraintModals(WrappedComponent) {
    class ConstraintModalWrapper extends Component {
        static displayName = `AddConstraintModals(${getDisplayName(WrappedComponent)})`;
        static WrappedComponent = WrappedComponent;

        static propTypes = {
            handleClickEvent: PropTypes.bool,
            isProfile: PropTypes.bool,
            obgynProcedures: PropTypes.arrayOf(procedureShape),
            onlyCheckResourceConstraint: PropTypes.bool,
            onTimeSlotClicked: PropTypes.func,
            orthopedicProcedures: PropTypes.arrayOf(procedureShape),
            provider: PropTypes.object,
            searchProcedureId: PropTypes.string,
            searchSpecialtyId: PropTypes.string,
            updateParameters: PropTypes.func,
        };

        static defaultProps = {
            // set to false to disable default on click behavior for wrapped component
            handleClickEvent: true,
            onlyCheckResourceConstraint: false,
            onTimeSlotClicked: noop,
            updateParameters: noop,
        };

        state = {
            modalType: null,
        };

        onClick = event => {
            if (this.props.handleClickEvent) {
                const modalType = checkConstraints({
                    isProfile: this.props.isProfile,
                    obgynProcedures: this.props.obgynProcedures,
                    orthopedicProcedures: this.props.orthopedicProcedures,
                    provider: this.props.provider,
                    searchProcedureId: this.props.searchProcedureId,
                    searchSpecialtyId: this.props.searchSpecialtyId,
                });

                if (modalType) {
                    event.preventDefault();
                }

                this.setState({
                    modalType,
                    primaryActionHref: event.currentTarget.href,
                });
            }
        };

        handleTimeSlotClick = (timeslot, event, searchResultId) => {
            const { onlyCheckResourceConstraint } = this.props;

            if (timeslot.constraint === ModalTypeIds.ASSISTANT) {
                event.preventDefault();
                this.setState({
                    modalType: ModalTypeIds.ASSISTANT,
                    primaryActionHref: timeslot.url,
                });
            } else if (!onlyCheckResourceConstraint) {
                this.onClick(event);
            }

            this.props.onTimeSlotClicked(timeslot, event, searchResultId);
        };

        onClickPrimaryAction = e => {
            e.stopPropagation();
        };

        onClickSecondaryAction = () => {
            const {
                provider,
                updateParameters,
            } = this.props;

            const { relevantSpecialty } = provider;

            const hasRelevantSpecialty = relevantSpecialty && relevantSpecialty.id;

            if (ExclusionModalTypeIds.has(this.state.modalType) && hasRelevantSpecialty) {
                updateParameters({ excludeSpecialties: [relevantSpecialty.id] });
            }

            this.setState({
                modalType: null,
            });
        };

        onClickCancel = () => {
            this.setState({
                modalType: null,
            });
        };

        maybeRenderConstraintModal() {
            if (!this.state.modalType) { return null; }

            return (
                <ConstraintModalContainer
                    modalType={this.state.modalType}
                    onClickCancel={this.onClickCancel}
                    onClickPrimaryAction={this.onClickPrimaryAction}
                    onClickSecondaryAction={this.onClickSecondaryAction}
                    primaryActionHref={this.state.primaryActionHref}
                    provider={this.props.provider}
                    searchProcedureId={this.props.searchProcedureId}
                    searchSpecialtyId={this.props.searchSpecialtyId}
                    showProcedureDropdown={this.state.showProcedureDropdown}
                    updateParameters={this.props.updateParameters}
                />
            );
        }

        render() {
            return (
                <React.Fragment>
                    {this.maybeRenderConstraintModal()}
                    <WrappedComponent
                        {...this.props}
                        onClick={this.onClick}
                        onTimeSlotClicked={this.handleTimeSlotClick}
                    />
                </React.Fragment>
            );
        }
    }

    return compose(
        withSpecialtyProcedures({
            specialtyId: SpecialtyIds.ORTHOPEDIC_SURGEON,
            mapResultToPropName: 'orthopedicProcedures',
        }),
        withSpecialtyProcedures({
            specialtyId: SpecialtyIds.OBGYN,
            mapResultToPropName: 'obgynProcedures',
        }),
    )(ConstraintModalWrapper);
}
